import * as THREE from 'three';
import type { Route, Vec3 } from '../types';
import type { PoseTransformService } from '../services/poseTransformService';
import { crumbRevealAheadM, distXZ, HeadingSmoother, nearestForwardSegmentIdx, pointToSegmentXZ, remainingRouteM, trailGuide, TurnAroundDetector, VerticalRateMonitor, type TrailGuide } from '../utils/math';
import { logEvent } from '../services/debugLog';
import { snapToNavmesh, isNavmeshLoaded } from '../services/navmeshService';
import { hasOccluder } from '../services/occluderService';
import { diagDisabled } from '../utils/diagnostics';

/**
 * AR navigation rendering: breadcrumb dots + path line + destination marker
 * (all authored in MAP SPACE under poseTransform.mapRoot) and a direction
 * arrow that floats in front of the camera pointing at the next waypoint.
 *
 * Kept deliberately light for mobile: shared geometries/materials, no
 * textures, no external models.
 */

const WAYPOINT_REACHED_M = 1.5; // advance to next node within this distance
const ARRIVAL_M = 2.0; // arrived when this close to the POI (horizontal)
/**
 * Arrival also requires vertical proximity: without this, standing directly
 * BELOW a POI on the floor above reads as "0 m away" in XZ and falsely
 * triggers arrival. 2 m covers same-floor camera-vs-POI height differences
 * while rejecting a full floor (~3 m).
 */
const ARRIVAL_MAX_DY_M = 2.0;
/**
 * Assumed height of the phone above the floor (metres). Used to project
 * breadcrumbs down to floor level from the camera's map-space position —
 * neither WebXR 'local' space nor 8th Wall gives us a real floor plane, and
 * route node Y values may be authored at furniture height (e.g. a POI marked
 * on a table). ~1.4 m is a typical hand-held phone height.
 */
const ASSUMED_PHONE_HEIGHT_M = 1.4;
/** Distance between breadcrumb discs along the smoothed path. */
// Step 3 (Mattercraft reference): a fine, dense, UNIFORM dotted-pin trail — the
// reference leads with closely-spaced small pins, not big chevrons + a giant
// arrow. Spacing 0.6 → 0.35 m and pin radius 0.18 → 0.10 m give that look.
const CRUMB_SPACING_M = 0.35;
/** Path reveal window (arc-length around the user's progress). Step 3 / user
 *  request ("lock the trail like the reference"): the reference shows the WHOLE
 *  path pinned to the floor, so it never disappears on a turn. Widened 8 → 25 m
 *  ahead + 2 → 4 m behind so the full trail stays visible; the depth occluder
 *  (restored ON) hides whatever is behind a wall, and #32 still clamps the
 *  reveal NEAR until the occluder has loaded (so no far through-wall crumbs at
 *  session start). */
const PATH_VISIBLE_AHEAD_M = 15;
const PATH_VISIBLE_BEHIND_M = 4;
/** Destination pin visibility (3D). Widened 10 → 25 m (user: "lock the
 *  destination like the reference") so the marker is persistent for the whole
 *  route. It stays depth-tested (occluded by walls) until the final 3.5 m, so a
 *  distant destination shows only when actually in line of sight — no
 *  through-wall clutter. Pulled 25 → 15 m (Test 19): far content swings ~(yaw×
 *  distance) on each correction, so a nearer pill sways less under drift. */
const PIN_VISIBLE_DIST_M = 15;
/** Arrow v2: how far ahead of the user's path-progress the arrow sits. */
const ARROW_LEAD_M = 2.2;

export interface GuidanceUpdate {
  distanceToNextM: number;
  /** Distance to the next actual TURN (bearing change > threshold), or null when the remaining path is essentially straight. Use this for "X m to next turn" — distanceToNextM is just the next path node (navmesh nodes are dense, so it jumps confusingly). */
  distanceToTurnM: number | null;
  /** Number of turns left on the remaining path (for "3 turns" previews). */
  turnsRemaining: number;
  /** Lateral distance from the user to the remaining path (off-route detection). */
  offPathM: number;
  /** Significant height change within the next ~8 m of path — prompt "take the stairs/elevator". */
  floorChange: 'up' | 'down' | null;
  distanceRemainingM: number;
  /** G5: true while the pose is untrusted (implausible vertical motion) — consumers must not arrive/announce from it. */
  poseSuspect: boolean;
  nextNodeIndex: number;
  arrived: boolean;
  /** Camera facing >120° away from the next waypoint — say "turn around". */
  turnAround: boolean;
  /** #36: where the trail is relative to the camera view — 'onscreen' when the
   *  crumbs are visible, else which way to turn to bring them back. */
  trailGuide: TrailGuide;
}

/** Bearing change (degrees) that counts as a real "turn" for the HUD. */
const TURN_ANGLE_DEG = 35;

export class NavigationOverlay {
  private group = new THREE.Group(); // map-space content
  private arrow: THREE.Group; // MAP-space path arrow (v2 — rides ON the path)
  // Arrow instrumentation (user report: "arrow shifts mid-route") — see update().
  private lastArrowBearing: number | null = null;
  private lastArrowT = 0;
  private lastArrowTarget = -1;
  private lastJumpLogT = 0;
  /** Exponentially-smoothed arrow pose (absorbs VPS anchor easing). */
  private arrowPos: THREE.Vector3 | null = null;
  /** #29: rate-capped so raw target-bearing jumps (leapfrog/recalc/approach) can never flick the rendered arrow. */
  private arrowYawSmoother = new HeadingSmoother();
  private route: Route | null = null;
  private nextIdx = 0;
  private arrived = false;
  /** Crumb holders with arc-length along the path, for progressive reveal. */
  private crumbs: Array<{ obj: THREE.Group; arc: number; pos: THREE.Vector3 }> = [];
  // Perf (2026-07-12 "FPS was higher before"): update() runs EVERY frame —
  // building fresh arrays/Vector3s each call feeds Safari's GC and shows up
  // as frame-time spikes. These buffers are reused instead.
  /** Route node positions + destination (refs; rebuilt only in setRoute). */
  private allPts: Vec3[] = [];
  /** Reused [user, ...remaining nodes, dest] buffer for the per-frame walk. */
  private walkPts: Vec3[] = [];
  private scratchCamDir = new THREE.Vector3();
  /** G5: latches while SLAM vertical motion is implausible (Test 3: a 2.4 m/s
   *  height dive during a VPS-blind descent fired "arrived" 5 m early). */
  private vertSanity = new VerticalRateMonitor();
  private vertSuspectLogged = false;
  /** G6: "Turn around" only after 1.5 s of SUSTAINED misalignment on a trusted pose. */
  private turnAroundHold = new TurnAroundDetector(1500);
  /** Foot-projected user position for remainingRouteM (G1) — reused, no per-frame allocs. */
  private scratchFoot = { x: 0, y: 0, z: 0 };
  private scratchTarget = new THREE.Vector3();
  private scratchCamPos = new THREE.Vector3();
  private scratchArrowT = new THREE.Vector3();
  private scratchFinalP = new THREE.Vector3();
  /** Last floor-change cue state, so we log a debug line only on transitions, not every frame. */
  private lastFloorChangeState: 'up' | 'down' | null = null;

  // shared resources
  // Breadcrumbs: uniform small NavMe-logo pins forming a dense dotted trail
  // (step 3, Mattercraft reference look — no chevron-at-turn variant).
  private crumbGeo = new THREE.CircleGeometry(0.1, 24);
  private crumbMat = makeCrumbMaterial('/logo.png');
  private destMat = new THREE.MeshBasicMaterial({ color: 0x22c55e });

  constructor(
    private poseTransform: PoseTransformService,
    scene: THREE.Scene,
    private camera: THREE.Camera,
  ) {
    // Map-anchored content lives under mapRoot (worldFromMap applied there).
    this.poseTransform.mapRoot.add(this.group);
    scene.add(this.poseTransform.mapRoot);

    // Arrow v2 (video 2026-07-12): lives in MAP space and rides ON the crumb
    // polyline (navmesh-snapped) — the old camera-space arrow aimed straight-
    // line at the next NODE, which pointed through walls/furniture, turned
    // into an edge-on sliver, or left the view entirely in the final metres.
    this.arrow = buildArrow();
    this.arrow.visible = false;
    this.poseTransform.mapRoot.add(this.arrow);
  }

  /**
   * @param route computed route (graph or direct-line fallback)
   * @param userMapY the user's current camera Y in map space — breadcrumbs
   *   are projected down from this to floor level so they lie on the ground
   *   regardless of what height the route nodes/POI were authored at.
   */
  setRoute(route: Route, userMapY?: number): void {
    this.clearRoute();
    this.route = route;
    this.allPts = [...route.nodes.map((n) => n.position), route.destination.position];
    this.nextIdx = 0;
    this.arrived = false;
    this.lastFloorChangeState = null;
    this.lastArrowBearing = null;
    this.lastArrowTarget = -1;
    this.arrowPos = null;
    this.arrowYawSmoother.reset();

    // Floor height for ground-based content. Navmesh routes carry real
    // per-point surface heights (multi-floor!), so respect their Y; other
    // route kinds are flattened to the user's estimated floor level —
    // EXCEPT cross-floor graph routes (stairs/elevator edges), whose node
    // heights span floors on purpose: flattening those would pin the whole
    // trail to the start floor.
    const ys = route.nodes.map((n) => n.position.y);
    const ySpan = ys.length > 0 ? Math.max(...ys) - Math.min(...ys) : 0;
    const followsSurface = route.nodes[0]?.kind === 'navmesh' || ySpan > 1.5;
    const floorY =
      userMapY !== undefined ? userMapY - ASSUMED_PHONE_HEIGHT_M : route.nodes[0]?.position.y ?? 0;

    // Smooth breadcrumb trail: Catmull-Rom spline through the waypoints,
    // sampled at even spacing — no connecting line, just evenly spaced
    // discs cycling through CRUMB_TEXTURES, lying flat on the floor.
    const waypoints = [
      ...route.nodes.map(
        (n) =>
          new THREE.Vector3(n.position.x, followsSurface ? n.position.y : floorY, n.position.z),
      ),
      new THREE.Vector3(
        route.destination.position.x,
        followsSurface ? route.destination.position.y : floorY,
        route.destination.position.z,
      ),
    ];
    let crumbPoints: THREE.Vector3[] = waypoints;
    if (waypoints.length >= 2) {
      const curve = new THREE.CatmullRomCurve3(waypoints, false, 'centripetal', 0.5);
      const count = Math.max(2, Math.round(curve.getLength() / CRUMB_SPACING_M));
      crumbPoints = curve.getSpacedPoints(count);
    }
    // The spline smooths corners GEOMETRICALLY — it doesn't know where walls
    // are, so rounded corners cut through them (user video, 2026-07-12:
    // crumbs inside walls). The navmesh DOES know: pull every sampled point
    // back onto the walkable surface. Distant snaps are rejected — a far
    // correction means the point sits over a scan hole, where the original
    // spline point is the lesser evil.
    if (followsSurface && isNavmeshLoaded()) {
      let pulled = 0;
      crumbPoints = crumbPoints.map((p) => {
        const s = snapToNavmesh({ x: p.x, y: p.y, z: p.z });
        if (!s) return p;
        const dx = s.x - p.x;
        const dz = s.z - p.z;
        const d2 = dx * dx + dz * dz;
        if (d2 > 1.5 * 1.5) return p; // over a scan hole — keep spline point
        if (d2 > 0.01) pulled++;
        return new THREE.Vector3(s.x, s.y, s.z);
      });
      if (pulled > 0) logEvent('route', `crumbs pulled onto navmesh: ${pulled}/${crumbPoints.length}`);
    }

    // Each crumb: flat disc, image's "up" oriented along the path toward the
    // destination, hidden until the user's progress reveals it (see update()).
    this.crumbs = [];
    let arc = 0;
    // Diagnostic (?no=crumbs): skip building the trail entirely for the
    // bare-navigation A/B. With no crumbs the arrow (which rides the trail) and
    // reveal loop also no-op.
    if (!diagDisabled('crumbs')) crumbPoints.forEach((p, i) => {
      if (i > 0) arc += p.distanceTo(crumbPoints[i - 1]);

      const holder = new THREE.Group();
      // 7 cm hover (was 2 cm): with the depth-only occluder ON, a few cm of
      // uncorrected vertical SLAM drift buried the discs under the scanned
      // floor — video 2026-07-12 showed the trail vanishing mid-route for
      // seconds, exactly while low-conf corrections were being held.
      holder.position.set(p.x, p.y + 0.07, p.z);

      // Yaw so the texture's up-axis points at the next path point.
      const next = crumbPoints[Math.min(i + 1, crumbPoints.length - 1)];
      const dx = next.x - p.x;
      const dz = next.z - p.z;
      if (dx * dx + dz * dz > 1e-6) holder.rotation.y = Math.atan2(-dx, -dz);

      // Uniform pins (reference look): every crumb is the same small pin.
      const mesh = new THREE.Mesh(this.crumbGeo, this.crumbMat);
      mesh.rotation.x = -Math.PI / 2; // lie flat; texture-up now faces local -Z
      holder.add(mesh);

      holder.visible = false; // revealed progressively in update()
      this.group.add(holder);
      this.crumbs.push({ obj: holder, arc, pos: p.clone() });
    });

    // Destination marker: floating pin (cone + ring). The RING reads through
    // the occluder (depthTest off) — in the final metres it's the "you're
    // here" beacon and must never be swallowed by a door/wall.
    const pin = new THREE.Group();
    const cone = new THREE.Mesh(new THREE.ConeGeometry(0.15, 0.4, 16), this.destMat);
    cone.rotation.x = Math.PI; // point down
    cone.position.y = 1.4;
    const ringMat = this.destMat.clone();
    ringMat.depthTest = false;
    ringMat.transparent = true;
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.03, 8, 24), ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.05;
    ring.renderOrder = 998;
    ring.name = 'destination-ring';
    // Reference "pill": a persistent floating name label above the destination,
    // billboarded to the camera and always readable (depthTest off), like the
    // reference's floating "Office" tag. Shows with the pin (≤ 25 m).
    const label = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: makeDestLabelTexture(route.destination.name), depthTest: false, transparent: true }),
    );
    label.scale.set(0.95, 0.27, 1); // world metres (canvas is ~3.5:1)
    label.position.y = 1.95; // just above the cone (y = 1.4)
    label.renderOrder = 1000;
    label.name = 'destination-label';
    pin.add(cone, ring);
    if (!diagDisabled('pill')) pin.add(label); // ?no=pill removes the floating name tag
    // Pin stands on the floor under the POI (POI itself may be authored at
    // furniture height); the cone floats at eye-ish level above it.
    // Navmesh routes carry the destination's REAL surface height — use it.
    // Flattening to the user's start-floor level put a cross-floor pin at
    // the wrong elevation (hovering mid-air / buried in the slab; user
    // report 2026-07-12: "destination marker is confusing").
    const pinY = followsSurface ? route.destination.position.y : floorY;
    pin.position.set(route.destination.position.x, pinY, route.destination.position.z);
    pin.name = 'destination-pin';
    this.group.add(pin);

    this.arrow.visible = !diagDisabled('arrow'); // ?no=arrow hides the heading arrow
  }

  clearRoute(): void {
    for (const child of [...this.group.children]) {
      this.group.remove(child);
      child.traverse((o) => {
        // dispose per-route geometries (shared geos/mats survive)
        if (o instanceof THREE.Line) o.geometry.dispose();
        if (o instanceof THREE.Mesh && o.geometry !== this.crumbGeo) o.geometry.dispose();
      });
    }
    this.route = null;
    this.allPts = [];
    this.arrow.visible = false;
    this.arrived = false;
    this.nextIdx = 0;
    this.crumbs = [];
  }

  /** True while a route is set and not yet arrived. */
  get isNavigating(): boolean {
    return this.route !== null && !this.arrived;
  }

  /** Hide/show guidance without discarding the route (tracking loss). */
  setGuidanceVisible(visible: boolean): void {
    this.arrow.visible = visible && this.route !== null && !this.arrived;
  }

  /** Soft arrival (VPS-blind zones): main.ts decided we're there — stop
   *  rendering guidance even though the geometric arrival never triggered. */
  forceArrived(): void {
    this.arrived = true;
    this.arrow.visible = false;
  }

  /**
   * Per-XR-frame update. Advances waypoints, orients the arrow, animates the
   * destination pin, and returns guidance numbers for the 2D HUD.
   */
  update(nowMs: number): GuidanceUpdate | null {
    if (!this.route || !this.poseTransform.isLocalized) return null;

    const pose = this.poseTransform.snapshot(this.camera);
    const user = pose.mapPosition;

    // Advance past reached waypoints — floor-gated (G4): a waypoint on
    // another floor can sit under the user's feet (Kitchen below the start
    // bedroom, issue #23) and must not count as "reached" through the slab.
    const userFootY = user.y - ASSUMED_PHONE_HEIGHT_M;
    // G5: while the height estimate is moving implausibly fast, the whole
    // pose is untrusted — no waypoint advancement, no leapfrog, no arrival,
    // no floor cues, until it stabilizes (or a VPS correction lands).
    const poseSuspect = this.vertSanity.update(userFootY, nowMs);
    if (poseSuspect && !this.vertSuspectLogged) {
      this.vertSuspectLogged = true;
      logEvent('pose', '⚠️ vertical motion implausible (>1.8 m/s sustained) — pose SUSPECT: arrival/floor-cues/leapfrog suspended until height stabilizes');
    } else if (!poseSuspect && this.vertSuspectLogged) {
      this.vertSuspectLogged = false;
      logEvent('pose', 'vertical motion stabilized — pose trusted again');
    }
    while (
      !poseSuspect &&
      this.nextIdx < this.route.nodes.length &&
      distXZ(user, this.route.nodes[this.nextIdx].position) < WAYPOINT_REACHED_M &&
      Math.abs(this.route.nodes[this.nextIdx].position.y - userFootY) <= 1.5
    ) {
      this.nextIdx++;
    }

    const dest = this.route.destination.position;

    // LEAPFROG (video 2026-07-12): a big pose correction can move the user
    // past several waypoints WITHOUT ever coming within reach of each — the
    // sequential loop above then leaves the arrow aiming at a stale node
    // beside/behind the user (frame showed arrow pointing 90° right while
    // crumbs + destination lay ahead-left). Project the user onto the
    // REMAINING polyline: when a segment strictly ahead of the current
    // target is the closest one (laterally within 3 m), jump the target
    // forward to that segment's end. Forward-only — never regresses.
    {
      const ptsAll = this.allPts; // refs cached in setRoute — no per-frame rebuild
      // Floor-aware forward search (G4, utils/math): segments on another
      // floor are invisible through the slab — issue #23 jumped the target
      // node 2 → 15 because the kitchen tail ran under the start bedroom.
      const best = poseSuspect ? -1 : nearestForwardSegmentIdx(user, userFootY, ptsAll, this.nextIdx);
      if (best >= this.nextIdx) {
        logEvent(
          'route',
          `waypoint leapfrog: target node ${this.nextIdx} → ${best + 1} (user near later same-floor segment)`,
        );
        this.nextIdx = best + 1;
      }
    }

    const target: Vec3 = this.nextIdx < this.route.nodes.length
      ? this.route.nodes[this.nextIdx].position
      : dest;

    // "Turn around" detection: the 3D arrow already points backwards in this
    // case, but the HUD/voice never said so (user report). World-space check
    // so no map/world frame conversion errors: camera forward vs. to-target.
    const camDir = this.scratchCamDir;
    this.camera.getWorldDirection(camDir);
    camDir.y = 0;
    const targetWorld = this.poseTransform.mapRoot.localToWorld(
      this.scratchTarget.set(target.x, target.y, target.z),
    );
    targetWorld.sub(this.camera.getWorldPosition(this.scratchCamPos));
    targetWorld.y = 0;
    const turnAroundRaw =
      targetWorld.lengthSq() > 1 && // ignore when basically on top of the waypoint
      camDir.lengthSq() > 1e-6 &&
      camDir.normalize().dot(targetWorld.normalize()) < -0.5; // >120°
    // G6 (#27, Tests 3+4: 4 false "Turn around" cues per walk, each during a
    // pose correction/target churn): the raw signal flickers — require it
    // to hold for 1.5 s on a TRUSTED, SETTLED pose before telling the user
    // to physically turn around. #28 (Test 5): also require the anchor to be
    // uncontested — a held (unconfirmed) correction means the pose is ~metres
    // wrong and its heading can't source a reverse command.
    const turnAround = this.turnAroundHold.update(
      turnAroundRaw,
      {
        suspect: poseSuspect,
        settling: this.poseTransform.isSettling,
        recentSnap: this.poseTransform.recentSnap(6000),
        holdingCorrection: this.poseTransform.holdingCorrection,
      },
      nowMs,
    );

    // #36 off-screen trail guidance (drift-tolerant): camDir + targetWorld are
    // already unit vectors here (normalized in the turn-around check above).
    // Signed bearing (+ = trail to the user's RIGHT) → coarse "which way to
    // turn" bucket. When the next waypoint is basically underfoot, don't nag.
    let trail: TrailGuide = 'onscreen';
    if (!this.arrived && targetWorld.lengthSq() > 1e-6 && camDir.lengthSq() > 1e-6) {
      const cross = camDir.x * targetWorld.z - camDir.z * targetWorld.x; // + = right
      const bearingToTrail = (Math.atan2(cross, camDir.dot(targetWorld)) * 180) / Math.PI;
      trail = distXZ(user, target) > 1.0 ? trailGuide(bearingToTrail) : 'onscreen';
    }

    // Remaining distance: to next node, then along remaining legs, then to
    // POI — in FULL 3D from the user's foot (G1, INVARIANTS.md): the old
    // distXZ sum made a storey of descent read as zero extra metres while
    // voice spoke the route's true 3D length ("kitchen is not 5 meters",
    // 21:16 session). Foot-projected so phone height doesn't add slant.
    this.scratchFoot.x = user.x;
    this.scratchFoot.y = user.y - ASSUMED_PHONE_HEIGHT_M;
    this.scratchFoot.z = user.z;
    const remaining = remainingRouteM(this.scratchFoot, this.route.nodes, this.nextIdx, dest);

    // Walk the remaining polyline once, extracting: distance to the next real
    // TURN, total turns remaining, lateral off-path distance, and any
    // significant height change in the next ~8 m (stairs/elevator prompt).
    let distanceToTurnM: number | null = null;
    let turnsRemaining = 0;
    let offPathM = Infinity;
    let floorChange: 'up' | 'down' | null = null;
    {
      const pts = this.walkPts; // reused buffer — refs only, no per-frame allocs
      pts.length = 0;
      pts.push(user);
      for (let i = this.nextIdx; i < this.route.nodes.length; i++) {
        pts.push(this.route.nodes[i].position);
      }
      pts.push(dest);
      let acc = 0;
      for (let i = 1; i < pts.length - 1; i++) {
        acc += distXZ(pts[i - 1], pts[i]);
        const b1 = Math.atan2(pts[i].x - pts[i - 1].x, pts[i].z - pts[i - 1].z);
        const b2 = Math.atan2(pts[i + 1].x - pts[i].x, pts[i + 1].z - pts[i].z);
        let dAng = ((b2 - b1) * 180) / Math.PI;
        dAng = ((dAng + 540) % 360) - 180; // normalize to [-180, 180]
        if (Math.abs(dAng) > TURN_ANGLE_DEG && acc > 0.5) {
          turnsRemaining++;
          if (distanceToTurnM === null) distanceToTurnM = acc;
        }
      }
      // Off-path: min lateral distance from user to remaining segments (XZ),
      // floor-gated (G4): measuring against an under-floor tail made a user
      // standing ON the upstairs path read 4.2–4.7 m "off-route" (issue
      // #23) and fire spurious recalcs. Segments with BOTH endpoints on
      // another floor are skipped; if nothing is left on this floor the
      // user counts as on-path (recalculating from above is worse).
      for (let i = 1; i < pts.length - 1; i++) {
        if (Math.abs(pts[i].y - userFootY) > 1.5 && Math.abs(pts[i + 1].y - userFootY) > 1.5) continue;
        offPathM = Math.min(offPathM, pointToSegmentXZ(user, pts[i], pts[i + 1]));
      }
      if (!Number.isFinite(offPathM)) offPathM = 0;
      // Floor change within the next ~8 m of path. Compare against the
      // user's estimated FLOOR level (camera/eye height minus the assumed
      // phone-carry height), NOT the raw camera y — route nodes are
      // floor-anchored (~0 m), while `user.y` is eye height (~1.5-1.9 m
      // above the floor). Comparing them directly put every route ~1.5 m
      // "below" the user, so this fired 'down' on almost every single route
      // regardless of any real floor change. Bug found via a debug report
      // (2026-07-11): user stayed on floor 0 the whole route to a same-floor
      // POI, and "Take the stairs down ahead" kept repeating throughout.
      let arc = 0;
      let floorChangeDetail = '';
      const userFloorY = userFootY;
      if (poseSuspect) arc = 8; // G5: skip the floor-change scan entirely — cues from a diving height estimate said "Head UP the stairs" mid-descent (Test 3)
      for (let i = 1; i < pts.length && arc < 8; i++) {
        arc += distXZ(pts[i - 1], pts[i]);
        const dyAhead = pts[i].y - userFloorY;
        if (dyAhead > 1.2 || dyAhead < -1.2) {
          floorChange = dyAhead > 0 ? 'up' : 'down';
          floorChangeDetail = `dy=${dyAhead.toFixed(2)}m at ${arc.toFixed(1)}m ahead (nodeY=${pts[i].y.toFixed(2)}, userFloorY=${userFloorY.toFixed(2)}, dest="${this.route.destination.name}" floor=${this.route.destination.floor})`;
          break;
        }
      }
      // Log only on transitions (armed/cleared/direction change) — update()
      // runs every animation frame, so logging unconditionally would flood
      // the 600-event ring buffer with nothing new to say.
      if (floorChange !== this.lastFloorChangeState) {
        logEvent(
          'route',
          floorChange ? `floor-change ${floorChange} cue armed: ${floorChangeDetail}` : 'floor-change cue cleared',
        );
        this.lastFloorChangeState = floorChange;
      }
    }

    if (
      !this.arrived &&
      !poseSuspect && // G5: never declare arrival from an implausible pose (Test 3: fired 5 m early mid-descent)
      distXZ(user, dest) < ARRIVAL_M &&
      Math.abs(user.y - dest.y) < ARRIVAL_MAX_DY_M
    ) {
      this.arrived = true;
      this.arrow.visible = false;
    }

    // --- Progressive path reveal: find the crumb nearest the user (full 3D,
    // so a floor below doesn't count as "near"), then show only the window
    // behind/ahead of that progress point.
    let nearestIdx = 0;
    let userArc = 0;
    if (this.crumbs.length > 0) {
      let bestSq = Infinity;
      for (let i = 0; i < this.crumbs.length; i++) {
        const c = this.crumbs[i].pos;
        const dSq =
          (user.x - c.x) * (user.x - c.x) +
          (user.y - c.y) * (user.y - c.y) +
          (user.z - c.z) * (user.z - c.z);
        if (dSq < bestSq) {
          bestSq = dSq;
          nearestIdx = i;
        }
      }
      userArc = this.crumbs[nearestIdx].arc;
      // While a correction glides in (rate-capped, up to ~4 s for a big
      // one), the trail is temporarily offset from the user — the normal
      // window can leave NOTHING in the camera's view right after a turn
      // (user video 2026-07-12: ~1.5 s crumb gap post-turn). Widen the
      // window until the world settles so part of the trail stays visible.
      const settling =
        this.poseTransform.isSettling || this.poseTransform.recentCorrection(0.8, 2500);
      const behindM = settling ? PATH_VISIBLE_BEHIND_M + 2 : PATH_VISIBLE_BEHIND_M;
      // #32: until the depth occluder has loaded, don't reveal far crumbs — with
      // no depth mask they render straight through walls/cabinets. Near crumbs
      // (+ the always-visible arrow) still guide; the full window returns once
      // occlusion can hide anything behind a wall.
      const aheadM = crumbRevealAheadM(hasOccluder(), settling, PATH_VISIBLE_AHEAD_M, 3);
      for (const c of this.crumbs) {
        c.obj.visible =
          !this.arrived &&
          c.arc >= userArc - behindM &&
          c.arc <= userArc + aheadM;
        // De-clutter (video 2026-07-12): discs right at the user's feet fill
        // the frame — shrink them smoothly within 2 m instead of full size.
        if (c.obj.visible) {
          const d = distXZ(user, { x: c.pos.x, y: c.pos.y, z: c.pos.z });
          const s = d < 2 ? 0.45 + 0.55 * (d / 2) : 1;
          c.obj.scale.setScalar(s);
        }
      }
    }

    // --- Arrow v2 (video 2026-07-12): flat on the floor, ON the crumb
    // polyline ~2.2 m ahead of the user's progress, pointing along the path
    // tangent. The polyline is navmesh-snapped, so the arrow can never point
    // through a wall, never renders edge-on at eye height, and stays in the
    // corridor the user is looking down all the way to the door. depthTest
    // is off (buildArrow) so it also reads through the occluder.
    if (this.arrow.visible && this.crumbs.length > 1) {
      let li = nearestIdx;
      const lead = userArc + ARROW_LEAD_M;
      while (li < this.crumbs.length - 1 && this.crumbs[li].arc < lead) li++;
      let p = this.crumbs[li].pos;
      // Tangent over a ±2-crumb window (~2.4 m): the ±1 window stepped the
      // raw bearing by >20° at corner crumbs (logged Δ22° jump, 2026-07-12).
      const q = this.crumbs[Math.min(li + 2, this.crumbs.length - 1)].pos;
      const prev = this.crumbs[Math.max(li - 2, 0)].pos;
      let dirX = q.x - prev.x;
      let dirZ = q.z - prev.z;

      // FINAL APPROACH (video 2026-07-12 #3: the last ~2 m showed NOTHING —
      // ~1 m world corrections near the end left the tiny remaining path
      // behind/beside the user). Within 3.5 m, forget the polyline: put the
      // arrow between the user and the DESTINATION, pointing straight at it
      // (it's close, and depthTest is off) — guidance persists to the spot.
      if (remaining < 3.5) {
        const floorY = this.crumbs[this.crumbs.length - 1].pos.y;
        const ddx = dest.x - user.x;
        const ddz = dest.z - user.z;
        const dd = Math.max(Math.hypot(ddx, ddz), 1e-3);
        const t = Math.min(1.4, dd * 0.6) / dd;
        p = this.scratchFinalP.set(user.x + ddx * t, floorY, user.z + ddz * t);
        dirX = ddx;
        dirZ = ddz;
      }

      const dtS = Math.min(0.1, Math.max(0.008, (nowMs - this.lastArrowT) / 1000));
      const k = 1 - Math.exp(-dtS / 0.3); // ~0.3 s time constant
      const targetPos = this.scratchArrowT.set(p.x, p.y + 0.12, p.z);
      if (!this.arrowPos) this.arrowPos = targetPos.clone();
      else this.arrowPos.lerp(targetPos, k);
      this.arrow.position.copy(this.arrowPos);

      if (dirX * dirX + dirZ * dirZ > 1e-6) {
        // #29: rate-capped slew — the RENDERED arrow can never turn faster than
        // HeadingSmoother's max rate, so a raw target-bearing discontinuity
        // (leapfrog / recalc / final-approach hand-off) becomes a smooth swing,
        // never a flick. (Video-verified: Test 6 raw jumps up to Δ127° rendered
        // as a ~0.6 s swing.)
        const yawT = Math.atan2(dirX, dirZ); // group +Z (tip) → tangent
        this.arrow.rotation.set(0, this.arrowYawSmoother.update(yawT, dtS), 0);
      }

      // Instrumentation ("blue arrow shifts mid-route"): log waypoint-target
      // advances, and RAW target-bearing discontinuities WITH their cause — so
      // reports show why the aim point moved, without crying wolf: #29 proved
      // these are absorbed by the slew above (the rendered arrow stays smooth),
      // so they are diagnostic, not "unexpected".
      const bearingDeg = (Math.atan2(dirX, dirZ) * 180) / Math.PI;
      if (this.nextIdx !== this.lastArrowTarget) {
        logEvent(
          'route',
          `arrow target → node ${Math.min(this.nextIdx, this.route.nodes.length)}/${this.route.nodes.length} (${distXZ(user, target).toFixed(1)}m away)`,
        );
        this.lastArrowTarget = this.nextIdx;
        this.lastArrowBearing = null; // bearing legitimately changes on advance
      } else if (this.lastArrowBearing !== null) {
        let d = bearingDeg - this.lastArrowBearing;
        d = ((d + 540) % 360) - 180;
        const dt = nowMs - this.lastArrowT;
        if (Math.abs(d) > 30 && dt < 800 && nowMs - this.lastJumpLogT > 2000) {
          this.lastJumpLogT = nowMs;
          const correcting = this.poseTransform.recentCorrection(0.3, 1500);
          const cause = correcting ? 'VPS anchor easing' : 'path re-aim (leapfrog/recalc/approach)';
          logEvent(
            'route',
            `arrow target bearing Δ${Math.abs(d).toFixed(0)}° in ${Math.round(dt)}ms @node ${this.nextIdx} — ${cause}; absorbed by arrow slew`,
          );
        }
      }
      this.lastArrowBearing = bearingDeg;
      this.lastArrowT = nowMs;
    }

    // --- Destination pin: only visible when actually near (3D distance —
    // being one floor below the kitchen shouldn't show its pin through the
    // ceiling); gentle bob + spin while shown.
    const pin = this.group.getObjectByName('destination-pin');
    if (pin) {
      // Gate on remaining ROUTE distance, not straight-line — straight-line
      // shows the pin through a wall while the walk is still 19 m. ALSO
      // require the user to be on the destination's floor: a pin one storey
      // below reads as a marker floating in/under the slab (user report
      // 2026-07-12) — until the floor matches, the floor-change cue and the
      // crumb trail are the guidance.
      const onDestFloor = Math.abs(user.y - ASSUMED_PHONE_HEIGHT_M - pin.position.y) < 2.0;
      pin.visible = !this.arrived && remaining < PIN_VISIBLE_DIST_M && onDestFloor;
      if (pin.visible) {
        pin.rotation.y = nowMs / 800;
        pin.children[0].position.y = 1.4 + Math.sin(nowMs / 500) * 0.08;
        // Final approach: the floor ring PULSES so the exact spot is
        // unmistakable in the last metres. Its through-wall rendering only
        // kicks in <3.5 m — at 6 m it hovered confusingly over furniture
        // between the user and the office (video 2026-07-12 #4).
        const ring = pin.getObjectByName('destination-ring') as THREE.Mesh | null;
        if (ring) {
          const s = remaining < 6 ? 1 + 0.35 * (0.5 + 0.5 * Math.sin(nowMs / 250)) : 1;
          ring.scale.setScalar(s);
          (ring.material as THREE.MeshBasicMaterial).depthTest = remaining >= 3.5;
        }
      }
    }

    return {
      distanceToNextM: distXZ(user, target),
      distanceToTurnM,
      turnsRemaining,
      offPathM,
      turnAround: turnAround && !this.arrived,
      trailGuide: trail,
      floorChange,
      distanceRemainingM: remaining,
      poseSuspect,
      nextNodeIndex: this.nextIdx,
      arrived: this.arrived,
    };
  }
}

/** Reference-style destination pill: a light rounded tag with the room name in
 *  dark text, drawn to a canvas → billboarded Sprite over the destination. */
function makeDestLabelTexture(name: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 148;
  const ctx = canvas.getContext('2d')!;
  const r = 46;
  const pad = 10;
  const w = canvas.width - pad * 2;
  const h = canvas.height - pad * 2;
  ctx.fillStyle = 'rgba(248, 250, 252, 0.96)'; // near-white pill
  ctx.beginPath();
  ctx.moveTo(pad + r, pad);
  ctx.arcTo(pad + w, pad, pad + w, pad + h, r);
  ctx.arcTo(pad + w, pad + h, pad, pad + h, r);
  ctx.arcTo(pad, pad + h, pad, pad, r);
  ctx.arcTo(pad, pad, pad + w, pad, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#0f172a'; // dark slate text
  ctx.font = 'bold 58px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const short = name.length > 18 ? `${name.slice(0, 17)}…` : name;
  ctx.fillText(short, canvas.width / 2, canvas.height / 2 + 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 4;
  return tex;
}

function makeCrumbMaterial(url: string): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    map: new THREE.TextureLoader().load(url),
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false, // avoid z-fighting artifacts against the real floor
  });
}

// pointToSegmentXZ lives in utils/math.ts (shared with the floor-aware leapfrog, G4).

/**
 * Flat chevron arrow whose tip faces local +Z.
 * (Object3D.lookAt points a non-camera object's +Z axis at the target.)
 */
function buildArrow(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({
    color: 0x38bdf8,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
    // v2: the arrow is THE primary guidance — always readable, even when the
    // path point sits just past a doorway (the depth occluder would
    // otherwise swallow it, like the destination pin in the 2026-07-12
    // video's final approach).
    depthTest: false,
  });
  const shape = new THREE.Shape();
  // Chevron in XY plane, tip at top; rotated to lie flat pointing -Z.
  shape.moveTo(0, 0.22);
  shape.lineTo(0.16, -0.1);
  shape.lineTo(0.07, -0.1);
  shape.lineTo(0.07, -0.22);
  shape.lineTo(-0.07, -0.22);
  shape.lineTo(-0.07, -0.1);
  shape.lineTo(-0.16, -0.1);
  shape.closePath();
  const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), mat);
  mesh.rotation.x = Math.PI / 2; // lie flat; local +Y (tip) now faces +Z
  mesh.renderOrder = 999; // draw over the occluder (depthTest off)
  g.add(mesh);
  g.scale.setScalar(1.0); // step 3: shrunk 2.2 → 1.0 so the dotted-pin trail leads (reference look); small heading pip, not a dominating arrow
  return g;
}
